import React, { useEffect, useRef, useState } from 'react';
import { Station, PodcastEpisode } from 'api/models';
import { createUseStyles } from 'react-jss';
import styles from './Collection.styles';
import classNames from 'classnames';
import { DisplayLayout } from './Collection.const';
import ShowMoreButton from '../ShowMoreButton';
import { isMobile } from 'util/device';
import { scrollY } from 'util/noscroll';
import { TITLE } from 'globalConst/const';
import CollectionModal from './CollectionModal';
import PodcastFold from '../PodcastFold';
import CollectionContext from './CollectionContext';

import { isCarousel, withBackground } from './Collection.util';

import Swimlane from '../Swimlane';
import CollectionTiles from './CollectionTiles';
import useFilterCollectionItems from './useFilterCollectionItems';
import * as analytics from 'analytics';
import { contains, isEmpty } from 'util/index';

export interface CollectionProps {
  contentTypeId: string;
  slug: string;
  uuid?: string;

  tileComponent: string;
  isComponentLoading?: boolean;

  playoutsDisabled?: boolean;
  isHighlighted?: boolean;
  items?: Array<Station | PodcastEpisode>;
  display?: string;
  showCollectionLink?: boolean;
  lightText?: boolean;

  setUpcomingTracks?: (track: Array<PodcastEpisode> | null) => void;
  error?: boolean;
  itemsPerRow?: number;
  mobileRows: number;
  rows: number;
  swipe?: boolean;
  title: string;
  description?: string;
  isExpandable?: boolean;
  showMore?: {
    variant: string;
    label: string;
  };
  tileSizes?: string;

  hideShowMore?: boolean;
  hideTitle?: boolean;
  showMoreButton?: string[];
  showMoreLabel?: string;
  showCount?: boolean;

  // Context values
  isInSearch?: boolean;
  isPodcastPage?: boolean;
  isInFold?: boolean;
  type?: string;
  modalIsOpen?: boolean;
  onCollectionClosed?: () => void;
  recentSearch?: boolean;
}
const SCROLL_OFFSET = 80;
const useStyles = createUseStyles(styles, { name: 'Collection' });

const Collection = (props: CollectionProps): JSX.Element => {
  const {
    contentTypeId,
    slug,
    uuid: collectionId,

    tileComponent,
    isComponentLoading,
    playoutsDisabled,
    isHighlighted,
    display,
    showCollectionLink = true,
    itemsPerRow,
    rows = 1,
    title,
    description,

    isExpandable,
    showMore = {},
    showMoreButton,
    showMoreLabel,
    swipe,

    hideShowMore = false,
    hideTitle = false,
    showCount = false,

    mobileRows = 1,

    items,

    isInSearch,
    isPodcastPage,
    modalIsOpen,
    isInFold,

    onCollectionClosed,
    recentSearch = false,
  } = props;
  const collectionRef: React.Ref<HTMLDivElement> = useRef();
  const classes = useStyles();

  const [failedItems, setFailedItems] = useState([]);
  const [isExpanded, setIsExpanded] = useState(false);
  const [collectionIsModalOpen, setCollectionIsModalOpen] = useState(false);
  const displayInCarousel = isCarousel(display);

  const { filteredItems, visibleItems, showMoreItems } = useFilterCollectionItems(
    props,
    failedItems,
    displayInCarousel,
    isExpanded
  );

  useEffect(() => {
    if (!modalIsOpen && collectionIsModalOpen) {
      toggleCollectionModal();
    }

    return () => {
      if (collectionIsModalOpen) {
        onCollectionClosed();
      }
    };
  }, [modalIsOpen]);

  if (!tileComponent || isEmpty(filteredItems)) {
    return null;
  }

  const ComponentWrapper = displayInCarousel ? Swimlane : CollectionTiles;

  const enableCollectionModal = !isComponentLoading && showCollectionLink && items && items.length > visibleItems;

  const isHome = analytics.getIsOnHome();

  const toggleCollectionModal = (key?: 'dismiss' | null) => {
    if (isExpandable) {
      if (key === 'dismiss' && !isExpanded) {
        return;
      }
      const nextIsExpanded = !isExpanded;
      setIsExpanded(nextIsExpanded);

      if (nextIsExpanded) {
        analytics.trackCollectionExpand(title);
      } else {
        // scroll to top of collection
        const { top } = collectionRef.current.getBoundingClientRect();
        scrollY(top - SCROLL_OFFSET);
        analytics.trackCollectionCollapse(title);
      }
      return;
    }

    const nextCollectionIsModalOpen = !collectionIsModalOpen;

    if (key === 'dismiss' && nextCollectionIsModalOpen) {
      return;
    }

    if (nextCollectionIsModalOpen) {
      analytics.trackCollectionExpand(title);
    } else {
      onCollectionClosed();
      analytics.trackCollectionCollapse(title);
    }
    setCollectionIsModalOpen(nextCollectionIsModalOpen);
  };
  const handleError = (slug: string) => {
    if (slug && !contains(failedItems, slug)) {
      setFailedItems([...failedItems, slug]);
    }
  };

  const withFold = display === DisplayLayout.CAROUSEL_WITH_FOLD && !isMobile;

  const retVal = (
    <CollectionContext.Provider
      value={{
        showNowPlayingInfo: !playoutsDisabled,
        isPodcastPage,
        isInSearch,
        recentSearch,
        withFold,
        isInFold,
      }}
    >
      <div
        ref={collectionRef}
        data-testid={`collection-${slug}`}
        className={classNames(classes.container, {
          [classes.carouselContainer]: displayInCarousel,
          [classes.containerBackground]: withBackground(display, contentTypeId) || isInFold,
        })}
      >
        <ShowMoreButton
          contentTypeId={contentTypeId}
          display={display}
          isCarousel={displayInCarousel}
          enableCollectionModal={enableCollectionModal || isExpandable}
          toggleCollectionModal={toggleCollectionModal}
          isExpandable={isExpandable}
          isExpanded={isExpanded}
          itemsNotShown={items && items.length - itemsPerRow * rows}
          showMore={showMore}
          hideShowMore={hideShowMore}
          showMoreButton={showMoreButton}
          showMoreLabel={showMoreLabel}
        >
          <>
            {(isComponentLoading || title || description) && (
              <div
                className={classNames(classes.header, {
                  [classes.hideHeader]: hideTitle,
                  [classes.removeHeader]: hideTitle && hideShowMore,
                })}
              >
                <div
                  className={classNames(classes.titleWrap, {
                    [classes.titleWrapMobile]: !enableCollectionModal && isHome && title !== TITLE.POPULAR_SEARCH,
                  })}
                >
                  <h2 className={classes.title}>
                    {title}
                    {showCount && !displayInCarousel && (
                      <span className={classes.count} data-testid="collection-count">
                        {' '}
                        {items && items.length}{' '}
                      </span>
                    )}
                  </h2>
                </div>
                {description && <p className={classes.description}>{description}</p>}
              </div>
            )}
            <div className={classes.collectionWrapper}>
              {/* FIX: should be included as regular props when Carousel is refactored to TS component */}
              <ComponentWrapper
                {...{
                  key: collectionId,
                  contentTypeId,
                  slug,
                  tileComponent,
                  swipe,
                  itemsPerRow,
                  mobileRows,
                  rows: isExpanded ? Number.MAX_SAFE_INTEGER : rows,
                  display,
                  items: filteredItems,
                  onError: handleError,
                  peekNextItem: true,
                  title,
                  collectionId,
                }}
              />
            </div>
          </>
        </ShowMoreButton>
        {collectionIsModalOpen && (
          <CollectionModal
            contentTypeId={contentTypeId}
            tileComponent={tileComponent}
            itemsPerRow={itemsPerRow}
            items={showMoreItems}
            title={title}
            display={display}
            toggleCollectionModal={toggleCollectionModal}
            isModalOpen={collectionIsModalOpen}
            onError={handleError}
          />
        )}
        {withFold && <PodcastFold collectionId={collectionId} items={filteredItems} />}
      </div>
    </CollectionContext.Provider>
  );

  if (isHighlighted) {
    return (
      <div data-testid={`collection-highlighted-${slug}`} className={classes.highlight}>
        {retVal}
      </div>
    );
  }
  return retVal;
};

export default Collection;
